import React, { useState, useEffect, useRef } from 'react';
import { Shield, Sparkles, Terminal, Activity, Play, CheckCircle2, Copy, Layers, Database, ArrowRight, BookOpen, Loader2, Globe, Monitor, Palette, Paintbrush, Zap, Wrench, ImageIcon } from 'lucide-react';

interface Chapter {
    id: string;
    title: string;
    narratorText: string;
    speaker: string;
    speakerText: string;
    bg: string;
    music: string;
}

const HGC_CHAPTERS: Chapter[] = [
    {
        id: "chapter_1_ohio",
        title: "Ohio Assembly Line",
        narratorText: "A man can be almost inappropriately attached to his car. The story of this car, named 1HGC begins in Ohio.",
        speaker: "Assembly Robot",
        speakerText: "1HGC was built by countless men, women and robots, both foreign and domestic.",
        bg: "bg_ega_ohio_plant.png",
        music: "RidersOnTheStorm"
    },
    {
        id: "chapter_2_bronx",
        title: "Bronx Driveway",
        narratorText: "He lived the first half of his life in the gentle hands of a church going security guard from the Bronx. He only drove to work and to church or so he said.",
        speaker: "Buddy",
        speakerText: "Buddy wanted to help move more Bronx residents to church on Sunday. Buddy needed more than a mid-size sedan.",
        bg: "bg_ega_buddy_driveway.png",
        music: "TakenCareOfBusiness"
    },
    {
        id: "chapter_3_uncle",
        title: "The Dark Road",
        narratorText: "My uncle picked me up at 4:00 AM on a Monday morning in his pristine sedan. I would ride with him down to New Jersey. Eventually I got behind the wheel. And I crashed it.",
        speaker: "Mom",
        speakerText: "I bought Buddy's car and kept it in my name for you to drive. You got a shot and I won't let you waste it.",
        bg: "bg_ega_uncle_crash.png",
        music: "StrangeDays"
    },
    {
        id: "chapter_4_woods",
        title: "The Toxic Woods",
        narratorText: "I worked in cleaning up illegally dumped toxic waste. Since the client would be paying I was allowed to expense 54 cents a mile. In 1HGC that added up.",
        speaker: "Shady Boss",
        speakerText: "You have to tape the sensors on the roof because the dust will cover the array and then it will rain, turning everything to cement.",
        bg: "bg_ega_toxic_woods.png",
        music: "TakenCareOfBusiness"
    },
    {
        id: "chapter_5_roof",
        title: "The Dirt Road",
        narratorText: "Before leaving the dumps of New Jersey, the boss had left me to clean up the sensor equipment. I placed the box on the roof of my car while I packed away other instruments. Without a moments thought I jumped in the car and raced away.",
        speaker: "1HGC",
        speakerText: "I slowed down and stopped and by the grace of god: There they sat.",
        bg: "bg_ega_roof_incident.png",
        music: "RidersOnTheStorm"
    },
    {
        id: "chapter_6_obamamobile",
        title: "Suburban Driveway",
        narratorText: "1HGC had almost 150,000 miles by 2008. There was a little rust above the rear wheel wells but everything else was immaculate.",
        speaker: "Obamamobile",
        speakerText: "The election of 2008 led to my very first bumper sticker. An Obama/Biden 08 sticker proudly displayed on the back. It officially became the Obamamobile.",
        bg: "bg_ega_obamamobile.png",
        music: "StrangeDays"
    },
    {
        id: "chapter_7_winter",
        title: "Winter Highway",
        narratorText: "By 2009 it had 215,000 miles on it. I still expensed 54 cents a mile. One incredibly dark and snowy winter day, I drove right past a massive multi-car accident.",
        speaker: "Obamamobile",
        speakerText: "1HGC didn't slide, he simply stopped, waited for the accident to clear, and then slowly drove away.",
        bg: "bg_ega_winter_drive.png",
        music: "RidersOnTheStorm"
    },
    {
        id: "chapter_8_hillbillies",
        title: "Mechanic's Bay",
        narratorText: "By 2011 I wanted a newer car. I gave the Civic to my wife. I packed the Obamamobile with all my possessions. I looked like the Beverly Hillbillies. The suspension bottomed out over every tiny bump.",
        speaker: "Mechanic",
        speakerText: "On the way, the check engine light came on. I found a random Irish mechanic. He asked me when was the last time the catalytic converter was replaced. Never.",
        bg: "bg_ega_hillbillies.png",
        music: "TakenCareOfBusiness"
    },
    {
        id: "chapter_9_long_island",
        title: "Demolition Site",
        narratorText: "By 2012 the Obamamobile had almost completely failed inside. I had to rip out the broken AC console. The tape deck was stuck. The entire interior console was a mess of loose, colored wires.",
        speaker: "The Shitbox",
        speakerText: "A friend needed an off road vehicle to drive around an old warehouse. The Obamamobile caught some serious air that day. We couldn't kill it.",
        bg: "bg_ega_long_island.png",
        music: "RidersOnTheStorm"
    },
    {
        id: "chapter_10_border",
        title: "Border Crossing",
        narratorText: "Eventually I started calling him The Shitbox. I moved to upstate New York. When the tags expired, I didn't want to get it re-inspected. So I just threw the license plates out. One day I drove my wife to the Canadian border... and took a wrong turn.",
        speaker: "Border Agent",
        speakerText: "Please pull into the inspection garage. Turn off the engine and step away from the vehicle.",
        bg: "bg_ega_border_crossing.png",
        music: "StrangeDays"
    }
];

export const GameMaker: React.FC = () => {
    const [gameId, setGameId] = useState('one_hgc_odyssey');
    const [platform, setPlatform] = useState<'web' | 'desktop'>('web');
    const [visualStyle, setVisualStyle] = useState<'ega' | 'vga' | 'cyberpunk'>('ega');
    const [protagonistImage, setProtagonistImage] = useState<string | null>(null);
    const [protagonistDescription, setProtagonistDescription] = useState('');
    const [plotIdea, setPlotIdea] = useState('');
    const [storyOutline, setStoryOutline] = useState(
        HGC_CHAPTERS.map(c => `[${c.title}]\nNarrator: ${c.narratorText}\n${c.speaker}: "${c.speakerText}"`).join('\n\n')
    );
    const [gameScope, setGameScope] = useState<'mini' | 'full' | 'grand'>('mini');
    const [isCompiling, setIsCompiling] = useState(false);
    const [isAnalyzingFace, setIsAnalyzingFace] = useState(false);
    const [logs, setLogs] = useState<string[]>([
        '[*] 90s Game Maker Studio initialized.',
        '[*] Select compile target and click Compile to package your adventure game.'
    ]);
    const [activeTab, setActiveTab] = useState<'build' | 'examples'>('build');
    const [selectedChapter, setSelectedChapter] = useState<Chapter>(HGC_CHAPTERS[0]);
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [selectedExample, setSelectedExample] = useState('1hgc');
    const [currentStep, setCurrentStep] = useState(0);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const terminalEndRef = useRef<HTMLDivElement>(null);

    const showToast = (msg: string) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(null), 5000);
    };

    const PRESETS = [
        {
            label: "🚗 1HGC Ohio Shitbox",
            id: "one_hgc_odyssey",
            style: "ega" as const,
            platform: "web" as const,
            scope: "mini" as const,
            plot: "A daring driver pilots a $500 junker across rural Ohio in a chaotic 24-hour endurance challenge.",
            outline: HGC_CHAPTERS.map(c => `[${c.title}]\nNarrator: ${c.narratorText}\n${c.speaker}: "${c.speakerText}"`).join('\n\n')
        },
        {
            label: "⚡ Cyberpunk Heist 2099",
            id: "cyberpunk_heist_2099",
            style: "cyberpunk" as const,
            platform: "web" as const,
            scope: "full" as const,
            plot: "A rogue netrunner infiltrates an orbital mega-corp server farm to extract an encrypted quantum memory core.",
            outline: "[Chapter 1: The Infiltration]\nNarrator: Rain pours over the neo-neon skyscrapers of Sector 4.\nNetrunner: \"Connection secured. The proxy nodes are bypassing security.\"\n\n[Chapter 2: The Core Room]\nNarrator: Sub-zero nitrogen cooling mist blankets the central server bank.\nSecurity AI: \"Unauthorized bio-signature detected. Initiating lockdown.\""
        },
        {
            label: "🚀 Deep Space Outpost",
            id: "deep_space_outpost_7",
            style: "vga" as const,
            platform: "desktop" as const,
            scope: "grand" as const,
            plot: "An engineer stranded on a subterranean lunar research facility must restore atmospheric life support.",
            outline: "[Chapter 1: Cryo Awakening]\nNarrator: Emergency amber lights flicker across the subterranean command pod.\nEngineer: \"Main oxygen scrubbers are offline. I need to reach Sector B.\"\n\n[Chapter 2: The Airlock]\nNarrator: Frost covers the pressure gauge as seismic tremors shake the facility.\nComputer: \"Warning: External atmospheric containment breach.\""
        }
    ];

    // Auto-compile on checkout redirect success
    useEffect(() => {
        const hash = window.location.hash || '';
        const search = window.location.search || '';
        const combined = hash + search;
        const params = new URLSearchParams(combined.split('?')[1] || '');
        if (params.get('success') === 'true' && params.get('session_id')) {
            runCompilation();
            const cleanHash = hash.split('?')[0];
            window.history.replaceState({}, document.title, window.location.pathname + cleanHash);
        }
    }, []);

    useEffect(() => {
        if (terminalEndRef.current) {
            terminalEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [logs]);

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setIsAnalyzingFace(true);
        setLogs(prev => [...prev, `[*] Ingesting face profile asset: ${file.name}...`]);

        const reader = new FileReader();
        reader.onload = (event) => {
            setProtagonistImage(event.target?.result as string);
            setTimeout(() => {
                setIsAnalyzingFace(false);
                setLogs(prev => [
                    ...prev,
                    `[+] Asset ingested successfully.`,
                    `[*] Extracting primary colors and portrait features...`,
                    `[+] AI Sprite Generator: Extracted retro dither profile.`
                ]);
                setProtagonistDescription(prev => prev || "EGA Retro Protagonist based on facial asset.");
                showToast("Ingested portrait asset for retro sprite generation!");
            }, 1500);
        };
        reader.readAsDataURL(file);
    };

    const runCompilation = () => {
        if (isCompiling) return;
        setIsCompiling(true);
        setLogs(prev => [...prev, `[*] Starting compilation for scope: ${gameScope}...`]);

        const compileSteps = [
            `[*] Parsing story outline for game: ${gameId}...`,
            `[*] Found narrative scenes. Aligning branching dialogue tree...`,
            `[*] Generating 16-color retro color lookups for ${visualStyle.toUpperCase()} visual style...`,
            `[*] Generating background templates for scenes...`,
            `[*] Assembling sprite sheets and player collision geometries...`,
            `[*] Linking MIDI audio assets: RidersOnTheStorm, TakenCareOfBusiness, StrangeDays...`,
            `[*] Invoking 90s Engine Builder pipeline (Pygbag WebAssembly compiler)...`,
            `[*] Compiling tilemaps, sound banks, and navigation scripts...`,
            `[+] HTML5 WebAssembly bundle built successfully.`,
            `[+] Saved package size: 2.14 MB.`,
            `[+] Cartridge ready for play: ${gameId}.wasm`
        ];

        let index = 0;
        const interval = setInterval(() => {
            if (index < compileSteps.length) {
                setLogs(prev => [...prev, compileSteps[index]]);
                index++;
            } else {
                clearInterval(interval);
                setIsCompiling(false);
                setLogs(prev => [...prev, `[+] Deployment complete! Game cartridge loaded in Play Demo Game tab.`]);
                showToast("🎮 Game Compiled Successfully! Switching to Play Demo Game...");
                setTimeout(() => {
                    setActiveTab('examples');
                    setSelectedChapter(HGC_CHAPTERS[0]);
                    setCurrentStep(0);
                }, 800);
            }
        }, 800);
    };

    const handleCompileAndCheckout = () => {
        if (isCompiling) return;
        setLogs(prev => [
            ...prev,
            `[*] Notice: AI story generation & Stripe live billing unwired for public showcase (Token Conservation Mode).`,
            `[*] Initializing 90s Adventure Engine spatial compiler in offline demonstration mode...`
        ]);
        runCompilation();
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 font-sans text-slate-100">
            {/* Header Status Bar */}
            <div className="glass-panel rounded-2xl p-6 mb-8 flex flex-col md:flex-row justify-between items-center gap-6">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-indigo-500/20 border border-indigo-400/50 flex items-center justify-center shadow-[0_0_15px_rgba(99,102,241,0.5)]">
                        <Sparkles className="w-6 h-6 text-indigo-300 animate-pulse" />
                    </div>
                    <div>
                        <h2 className="text-3xl font-extrabold text-white text-glow">
                            SlabLogic Game Maker
                        </h2>
                        <p className="text-indigo-300/80 text-xs font-mono mt-1 font-bold">slablogic.io/game-maker</p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="bg-slate-950 px-3 py-1.5 rounded-lg border border-emerald-500/20 flex items-center gap-2">
                        <Activity className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
                        <span className="text-[10px] font-mono text-emerald-400">FAST_MCP STATUS: ACTIVE</span>
                    </div>
                    <div className="bg-slate-950 px-3 py-1.5 rounded-lg border border-amber-500/20 flex items-center gap-2">
                        <Shield className="w-3.5 h-3.5 text-amber-400" />
                        <span className="text-[10px] font-mono text-amber-400">AI ENGINE: UNWIRED (TOKEN SAVER)</span>
                    </div>
                </div>
            </div>

            {/* Toast Notification */}
            {toastMessage && (
                <div className="max-w-xl mx-auto mb-6 p-4 bg-indigo-600 text-white rounded-2xl shadow-xl flex items-center justify-between border border-indigo-400/40 animate-fadeIn">
                    <span className="text-xs font-bold">{toastMessage}</span>
                    <button onClick={() => setToastMessage(null)} className="text-indigo-200 hover:text-white font-bold ml-3 text-xs">✕</button>
                </div>
            )}

            {/* Inner Navigation Tabs */}
            <div className="flex justify-center mb-8">
                <div className="bg-slate-900/50 p-1.5 rounded-2xl inline-flex border border-slate-800">
                    <button
                        onClick={() => setActiveTab('build')}
                        className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === 'build' ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.5)]' : 'text-slate-400 hover:text-white hover:bg-slate-800/50'}`}
                    >
                        Builder Workspace
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab('examples');
                            setSelectedChapter(HGC_CHAPTERS[0]);
                            setCurrentStep(0);
                        }}
                        className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === 'examples' ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.5)]' : 'text-slate-400 hover:text-white hover:bg-slate-800/50'}`}
                    >
                        Play Demo Game
                    </button>
                </div>
            </div>

            {/* Content Switcher */}
            {activeTab === 'build' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Form Input Section */}
                    <div className="lg:col-span-1 flex flex-col gap-6">
                        {/* Preset Selector Card */}
                        <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4 space-y-3">
                            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                                Quick Story Blueprint Presets
                            </label>
                            <div className="grid grid-cols-1 gap-2">
                                {PRESETS.map((preset) => (
                                    <button
                                        key={preset.id}
                                        type="button"
                                        onClick={() => {
                                            setGameId(preset.id);
                                            setVisualStyle(preset.style);
                                            setPlatform(preset.platform);
                                            setGameScope(preset.scope);
                                            setPlotIdea(preset.plot);
                                            setStoryOutline(preset.outline);
                                            showToast(`Loaded "${preset.label}" blueprint!`);
                                        }}
                                        className={`p-2.5 rounded-xl border text-xs font-semibold flex items-center justify-between transition-all ${gameId === preset.id ? 'bg-indigo-600/30 border-indigo-500 text-white' : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:text-white hover:bg-slate-900'}`}
                                    >
                                        <span>{preset.label}</span>
                                        <span className="text-[10px] font-mono uppercase bg-slate-900 px-2 py-0.5 rounded border border-slate-800">{preset.style}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6">
                            <h3 className="text-xl font-extrabold mb-4 flex items-center gap-2 text-indigo-300">
                                <Layers className="w-5 h-5" /> Compile Target
                            </h3>
                            <div className="flex flex-col gap-4">
                                <div>
                                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Game Identifier</label>
                                    <input
                                        type="text"
                                        value={gameId}
                                        onChange={(e) => setGameId(e.target.value)}
                                        className="w-full bg-slate-950 border border-slate-800 focus:border-indigo-500 rounded-xl px-4 py-2.5 text-slate-200 font-mono outline-none transition-all text-sm"
                                        placeholder="game_id_slug"
                                    />
                                </div>

                                <div>
                                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Deployment Target</label>
                                    <div className="grid grid-cols-2 gap-2 bg-slate-950 p-1 rounded-xl border border-slate-800">
                                        <button
                                            title="Compile into an HTML5/WebAssembly bundle playable directly in a browser"
                                            onClick={() => setPlatform('web')}
                                            className={`py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${platform === 'web' ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.5)]' : 'text-slate-500 hover:text-slate-300'}`}
                                        >
                                            <Globe className="w-4 h-4" /> Web
                                        </button>
                                        <button
                                            title="Compile into a standalone executable app for Desktop (Windows/Mac/Linux)"
                                            onClick={() => setPlatform('desktop')}
                                            className={`py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${platform === 'desktop' ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.5)]' : 'text-slate-500 hover:text-slate-300'}`}
                                        >
                                            <Monitor className="w-4 h-4" /> Desktop
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 block">Visual Aesthetic</label>
                                    <div className="grid grid-cols-3 gap-2 bg-slate-950 p-1 rounded-xl border border-slate-800">
                                        <button
                                            title="16-color retro palette (circa 1988) with heavy dithering and sharp pixel edges"
                                            onClick={() => setVisualStyle('ega')}
                                            className={`py-2 px-1 rounded-lg text-[10px] sm:text-xs font-bold transition-all flex flex-col items-center justify-center gap-1 ${visualStyle === 'ega' ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.5)]' : 'text-slate-500 hover:text-slate-300'}`}
                                        >
                                            <Palette className="w-4 h-4" /> EGA
                                        </button>
                                        <button
                                            title="256-color palette (circa 1992) with soft anti-aliased edges and vibrant tones"
                                            onClick={() => setVisualStyle('vga')}
                                            className={`py-2 px-1 rounded-lg text-[10px] sm:text-xs font-bold transition-all flex flex-col items-center justify-center gap-1 ${visualStyle === 'vga' ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.5)]' : 'text-slate-500 hover:text-slate-300'}`}
                                        >
                                            <Paintbrush className="w-4 h-4" /> SCUMM
                                        </button>
                                        <button
                                            title="High-contrast dark mode aesthetic with vivid neon and synthwave colors"
                                            onClick={() => setVisualStyle('cyberpunk')}
                                            className={`py-2 px-1 rounded-lg text-[10px] sm:text-xs font-bold transition-all flex flex-col items-center justify-center gap-1 ${visualStyle === 'cyberpunk' ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(79,70,229,0.5)]' : 'text-slate-500 hover:text-slate-300'}`}
                                        >
                                            <Zap className="w-4 h-4" /> Neon
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center justify-between">
                                        Protagonist Profile (Vision Gen)
                                        {isAnalyzingFace && <Activity className="w-3.5 h-3.5 text-indigo-400 animate-spin" />}
                                    </label>
                                    
                                    <div className="bg-slate-950 border border-slate-800 rounded-xl p-3 flex flex-col gap-3">
                                        <div className="flex items-center gap-3">
                                            <div 
                                                className="w-16 h-16 rounded-lg bg-slate-900 border border-slate-700 flex items-center justify-center overflow-hidden cursor-pointer hover:border-indigo-500 transition-colors shrink-0"
                                                onClick={() => fileInputRef.current?.click()}
                                            >
                                                {protagonistImage ? (
                                                    <img src={protagonistImage} alt="Protagonist" className="w-full h-full object-cover" />
                                                ) : (
                                                    <Sparkles className="w-5 h-5 text-slate-500" />
                                                )}
                                                <input 
                                                    type="file" 
                                                    ref={fileInputRef} 
                                                    onChange={handleImageUpload} 
                                                    accept="image/*" 
                                                    className="hidden" 
                                                />
                                            </div>
                                            
                                            <div className="flex-1">
                                                <textarea
                                                    value={protagonistDescription}
                                                    onChange={(e) => setProtagonistDescription(e.target.value)}
                                                    rows={2}
                                                    className="w-full bg-slate-900 border border-slate-800 focus:border-indigo-500 rounded-lg px-3 py-2 text-slate-200 text-xs outline-none transition-all resize-none"
                                                    placeholder="Upload a photo or describe the main character. The AI will extract physical traits to construct the master spritesheet."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-slate-900/60 border border-indigo-950 rounded-2xl p-4 shadow-lg mb-2">
                                    <label className="text-xs font-semibold text-indigo-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                                        <Sparkles className="w-4 h-4" /> AI Story Architect
                                    </label>
                                    <p className="text-[10px] text-slate-400 mb-3">Provide a free-text concept. The developer AI will automatically handle the frontend elements (Visual Style, Game ID, and Verbatim Outline).</p>
                                    <div className="flex flex-col gap-3">
                                        <textarea
                                            value={plotIdea}
                                            onChange={(e) => setPlotIdea(e.target.value)}
                                            rows={2}
                                            className="w-full bg-slate-950 border border-indigo-900/50 focus:border-indigo-500 rounded-xl px-3 py-2 text-slate-200 text-xs outline-none transition-all resize-none"
                                            placeholder="e.g. A cyber-hacker breaks into a mega-corporation but finds a ghost in the machine..."
                                        />
                                        <button
                                            onClick={() => {
                                                if (!plotIdea.trim()) return;
                                                setLogs(prev => [...prev, `[*] Querying AI Story Architect for concept: "${plotIdea.substring(0, 40)}..."`]);
                                                setTimeout(() => {
                                                    setGameId(plotIdea.toLowerCase().replace(/[^a-z0-9]+/g, '_').substring(0, 20));
                                                    setLogs(prev => [
                                                        ...prev,
                                                        `[+] Outline generated automatically!`,
                                                        `[*] Configured target game ID to: ${gameId}`
                                                    ]);
                                                }, 1200);
                                            }}
                                            className="w-full py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-all"
                                        >
                                            Generate Story Blueprint
                                        </button>
                                        <div className="mt-2 text-[10px] text-slate-450 bg-slate-950 p-2.5 rounded-lg border border-slate-900 leading-normal">
                                            💡 <strong>SlabLogic Integration:</strong> You can export this story outline and character specs to <a href="https://theirloom.io" target="_blank" rel="noopener noreferrer" className="text-rose-400 font-bold hover:underline">Theirloom</a> to render it as a full-color heirloom dossier or physical graphic novel.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Dialogue Outline & Terminal Section */}
                    <div className="lg:col-span-2 flex flex-col gap-6">
                        <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6 flex flex-col h-[400px]">
                            <h3 className="text-xl font-extrabold mb-4 flex items-center gap-2 text-indigo-300">
                                <BookOpen className="w-5 h-5" /> Narrative Outline & Dialogue Tree
                            </h3>
                            <textarea
                                value={storyOutline}
                                onChange={(e) => setStoryOutline(e.target.value)}
                                className="flex-1 w-full bg-slate-950 border border-slate-800 focus:border-indigo-500 rounded-xl p-4 text-slate-200 font-mono text-xs outline-none transition-all resize-none leading-relaxed"
                            />
                        </div>

                        {/* Compiler Config & Actions */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Scope Selector */}
                            <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 flex flex-col justify-between">
                                <div>
                                    <label className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider mb-1 block">Game Scope</label>
                                    <h4 className="text-sm font-extrabold text-white mb-2 uppercase">{gameScope} Campaign</h4>
                                    <p className="text-[10px] text-slate-400">Determines depth of dialog branches and rendering stages.</p>
                                </div>
                                <div className="grid grid-cols-3 gap-1 bg-slate-950 p-1 rounded-lg border border-slate-800 mt-4">
                                    <button onClick={() => setGameScope('mini')} className={`py-1 rounded text-[10px] font-bold ${gameScope === 'mini' ? 'bg-indigo-600 text-white' : 'text-slate-500'}`}>Mini</button>
                                    <button onClick={() => setGameScope('full')} className={`py-1 rounded text-[10px] font-bold ${gameScope === 'full' ? 'bg-indigo-600 text-white' : 'text-slate-500'}`}>Full</button>
                                    <button onClick={() => setGameScope('grand')} className={`py-1 rounded text-[10px] font-bold ${gameScope === 'grand' ? 'bg-indigo-600 text-white' : 'text-slate-500'}`}>Grand</button>
                                </div>
                            </div>

                            {/* Compilation Status Box */}
                            <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 flex flex-col justify-between">
                                <div>
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block">Compiling Progress</label>
                                    <div className="flex items-center gap-2 mt-2">
                                        {isCompiling ? (
                                            <>
                                                <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
                                                <span className="text-xs font-mono text-indigo-300">COMPILING...</span>
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                                <span className="text-xs font-mono text-emerald-400">READY</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="text-[10px] text-slate-500 font-mono mt-4">
                                    OUTPUT TYPE: WASM/HTML5
                                </div>
                            </div>

                            {/* Launch Compile Button */}
                            <button
                                onClick={handleCompileAndCheckout}
                                disabled={isCompiling}
                                className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl p-6 font-bold flex flex-col items-center justify-center gap-2 transition-all shadow-lg hover:scale-[1.02] active:scale-[0.98] border border-indigo-400/20"
                            >
                                <Play className="w-6 h-6 fill-white" />
                                <span className="text-sm">Compile Adventure Game</span>
                                <span className="text-[9px] font-mono text-indigo-200">Local Pygbag Compiler</span>
                            </button>
                        </div>

                        {/* Compiler Console Output Terminal */}
                        <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 font-mono text-xs h-[180px] flex flex-col">
                            <div className="flex items-center justify-between border-b border-slate-800 pb-2 mb-2 text-slate-400">
                                <span className="flex items-center gap-1.5"><Terminal className="w-4 h-4" /> COMPILER OUT</span>
                                <button onClick={() => setLogs([])} className="hover:text-white transition-colors text-[10px]">CLEAR</button>
                            </div>
                            <div className="flex-1 overflow-y-auto flex flex-col gap-1 text-slate-300 select-text">
                                {logs.map((log, i) => (
                                    <div key={i} className={log.startsWith('[!]') ? 'text-rose-400' : log.startsWith('[+') ? 'text-emerald-400' : 'text-slate-300'}>
                                        {log}
                                    </div>
                                ))}
                                <div ref={terminalEndRef} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Gallery/Demo View */}
            {activeTab === 'examples' && (
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    {/* Chapters List */}
                    <div className="lg:col-span-1 flex flex-col gap-2">
                        <h3 className="text-lg font-bold text-slate-300 mb-4 px-2">Game Scenes</h3>
                        {HGC_CHAPTERS.map((chap, i) => (
                            <button
                                key={chap.id}
                                onClick={() => {
                                    setSelectedChapter(chap);
                                    setCurrentStep(i);
                                }}
                                className={`w-full text-left p-3.5 rounded-xl border font-bold transition-all text-xs flex items-center justify-between ${currentStep === i ? 'bg-indigo-600/20 border-indigo-500 text-white shadow-lg' : 'bg-slate-900/40 border-slate-800/80 text-slate-400 hover:text-slate-200 hover:bg-slate-800/30'}`}
                            >
                                <span>{i + 1}. {chap.title}</span>
                                <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                        ))}
                    </div>

                    {/* Retro Arcade Gameplay Screen */}
                    <div className="lg:col-span-3 flex flex-col gap-6">
                        <div className="bg-slate-950 border-4 border-slate-800 rounded-3xl p-6 shadow-2xl relative overflow-hidden flex flex-col aspect-video select-none">
                            {/* CRT Ambient Light & Grid Overlay */}
                            <div className="absolute inset-0 bg-grid-pattern opacity-10 mix-blend-overlay pointer-events-none z-10"></div>
                            <div className="absolute inset-0 bg-gradient-to-b from-indigo-900/5 via-transparent to-indigo-900/5 pointer-events-none z-10 animate-scanline"></div>

                            {/* Active Chapter Details */}
                            <div className="flex-1 flex flex-col justify-between relative z-10 font-mono">
                                {/* Header Info */}
                                <div className="flex justify-between items-center bg-slate-900/80 border border-slate-800 px-4 py-2 rounded-xl text-glow">
                                    <span className="text-[10px] text-indigo-400 font-bold uppercase tracking-wider">SCENE: {selectedChapter.title}</span>
                                    <span className="text-[10px] text-slate-400 font-bold">AUDIO: {selectedChapter.music}.mid</span>
                                </div>

                                {/* Graphical Stage / Background (Simulated EGA pixel art style) */}
                                <div className="my-4 flex-1 rounded-2xl bg-indigo-950/40 border border-indigo-900/30 overflow-hidden relative flex items-center justify-center p-4">
                                    {/* Neon grid floor or lines */}
                                    <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-indigo-500/20 to-transparent border-t border-indigo-500/30 flex justify-center items-end p-2">
                                        <div className="text-[8px] text-indigo-400 font-mono tracking-widest font-semibold">EGA GRAPHICS SUBSYSTEM ACTIVE</div>
                                    </div>

                                    {/* Content display */}
                                    <div className="text-center max-w-lg relative z-20">
                                        <p className="text-slate-200 text-sm font-semibold leading-relaxed drop-shadow-md">
                                            {selectedChapter.narratorText}
                                        </p>
                                    </div>
                                </div>

                                {/* Dialogue & Subtitles box */}
                                <div className="bg-slate-900/90 border border-slate-800 p-4 rounded-xl">
                                    <div className="text-[10px] text-emerald-400 font-bold uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping"></span>
                                        {selectedChapter.speaker}
                                    </div>
                                    <p className="text-xs text-slate-300 italic font-semibold leading-relaxed">
                                        "{selectedChapter.speakerText}"
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Navigation / Action bar */}
                        <div className="flex justify-between items-center gap-4 bg-slate-900/30 border border-slate-800 p-4 rounded-2xl">
                            <div className="text-xs text-slate-400 font-mono">
                                Chapter {currentStep + 1} of {HGC_CHAPTERS.length}
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        const prev = Math.max(0, currentStep - 1);
                                        setCurrentStep(prev);
                                        setSelectedChapter(HGC_CHAPTERS[prev]);
                                    }}
                                    disabled={currentStep === 0}
                                    className="px-5 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold transition-all"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => {
                                        const next = Math.min(HGC_CHAPTERS.length - 1, currentStep + 1);
                                        setCurrentStep(next);
                                        setSelectedChapter(HGC_CHAPTERS[next]);
                                    }}
                                    disabled={currentStep === HGC_CHAPTERS.length - 1}
                                    className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-xs font-bold transition-all"
                                >
                                    Next Scene
                                </button>
                            </div>
                        </div>

                        {/* Theirloom Sticky Footer CTA Banner */}
                        <div className="bg-slate-950/60 border border-rose-500/30 rounded-2xl p-4 shadow-[0_10px_30px_rgba(244,63,94,0.15)] flex flex-col md:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-3 text-center md:text-left">
                                <div className="p-2 bg-rose-500/10 rounded-xl border border-rose-500/20 text-rose-400">
                                    <Sparkles className="w-5 h-5 animate-pulse" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-sm text-slate-200">Want to turn your adventure story into a physical masterpiece?</h4>
                                    <p className="text-xs text-slate-400 mt-0.5">Weave your game outlines, character profiles, and dialogue scripts into custom illustrated graphic novels or archival legacy books with <strong className="text-rose-400 font-semibold">Theirloom</strong>.</p>
                                </div>
                            </div>
                            <a 
                                href="https://theirloom.io" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="w-full md:w-auto px-5 py-2 bg-gradient-to-r from-rose-600 to-rose-700 hover:from-rose-500 hover:to-rose-600 text-white text-xs font-bold rounded-xl shadow-lg shadow-rose-950/50 flex items-center justify-center gap-1.5 transition-all hover:scale-[1.03]"
                            >
                                Try Theirloom →
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
